import React, { useState, useEffect } from 'react';
import {
  updateErrors,
  getErrorMessage as getErrorMessageFromErrors,
  isValidEmail,
} from '@utils/commons';
import { FormError } from '@typings/common';
import NewErrorSummary from '@components/NewErrorSummary';
import getErrorMessage from '@utils/errorUtils';
import { useNavigate } from 'react-router-dom';
import { changeEmail } from '@api/auth';

const UpdateEmailAddress = () => {
  const [emailDetails, setEmailDetails] = useState({
    currentEmail: '',
    newEmail: '',
    confirmEmail: '',
  });
  const [emailErrMessage, setEmailErrMessage] = useState<{
    currentEmail: string | undefined;
    newEmail: string | undefined;
    confirmEmail: string | undefined;
  }>({
    currentEmail: '',
    newEmail: '',
    confirmEmail: '',
  });
  const [changeEmailLoading, setChangeEmailLoading] = useState(false);
  const [errors, setErrors] = useState<FormError[]>([]);
  const navigate = useNavigate();

  const validateEmail = (value: string, customText: string) => {
    if (!value) {
      return `Please enter ${customText} address`;
    }

    if (!isValidEmail(value)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const checkMatchEmail = (newEmail: string, confirmEmail: string) => {
    if (newEmail.trim() !== confirmEmail.trim()) {
      return 'Email addresses must be the same';
    }
    return '';
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();

    let validationErrors: FormError[] = [];

    validationErrors = updateErrors(
      validationErrors,
      'currentEmail',
      validateEmail(emailDetails.currentEmail, 'your current email')
    );

    validationErrors = updateErrors(
      validationErrors,
      'newEmail',
      validateEmail(emailDetails.newEmail, 'a new email')
    );

    validationErrors = updateErrors(
      validationErrors,
      'confirmEmail',
      !emailDetails.confirmEmail ? 'Please re-enter your new email address' : ''
    );

    if (
      !validateEmail(emailDetails.newEmail, 'a new email') &&
      emailDetails.confirmEmail
    ) {
      validationErrors = updateErrors(
        validationErrors,
        'confirmEmail',
        checkMatchEmail(emailDetails.newEmail, emailDetails.confirmEmail)
      );
    }
    setErrors(validationErrors);

    if (validationErrors.length !== 0) {
      return;
    }

    try {
      setChangeEmailLoading(true);
      const res = await changeEmail(
        emailDetails.currentEmail,
        emailDetails.newEmail
      );
      if (res?.status === 'success') {
        navigate(`/check-your-email/?email=${emailDetails.newEmail}`);
      }
    } catch (error) {
      console.error(error);
      const errorMessage = getErrorMessage(error);
      validationErrors = updateErrors(
        validationErrors,
        'changeEmail',
        errorMessage
      );
      setErrors(validationErrors);
    } finally {
      setChangeEmailLoading(false);
    }
  };

  const onResetForm = (e: any) => {
    e.preventDefault();
    setEmailDetails({
      currentEmail: '',
      newEmail: '',
      confirmEmail: '',
    });
  };

  useEffect(() => {
    setEmailErrMessage({
      currentEmail: getErrorMessageFromErrors(errors, 'currentEmail'),
      newEmail: getErrorMessageFromErrors(errors, 'newEmail'),
      confirmEmail: getErrorMessageFromErrors(errors, 'confirmEmail'),
    });
  }, [errors]);

  return (
    <div className="mt-10 govuk-bp:mt-14 mb-10">
      <h1 className="govuk-heading-l">Updating your email address</h1>
      <p className="govuk-body">
        If you would like to change your email address please enter the details
        requested below.
      </p>
      <form>
        {errors.length > 0 && <NewErrorSummary errors={errors} />}
        <div
          className={`govuk-form-group ${emailErrMessage.currentEmail ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="currentEmail">
            Current email address
          </label>
          {emailErrMessage.currentEmail && (
            <span id="email-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>{' '}
              {emailErrMessage.currentEmail}
            </span>
          )}
          <input
            className={`govuk-input govuk-input--width-20 ${emailErrMessage.currentEmail ? 'govuk-input--error' : ''}`}
            id="currentEmail"
            name="currentEmail"
            type="email"
            spellCheck="false"
            aria-describedby={
              emailErrMessage.currentEmail ? 'current-email-error' : undefined
            }
            autoComplete="email"
            placeholder="Enter current email address"
            value={emailDetails.currentEmail}
            onChange={e =>
              setEmailDetails(prev => ({
                ...prev,
                currentEmail: e.target.value,
              }))
            }
          />
        </div>
        <div
          className={`govuk-form-group ${emailErrMessage.newEmail ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="newEmail">
            New email address
          </label>
          {emailErrMessage.newEmail && (
            <span id="email-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>{' '}
              {emailErrMessage.newEmail}
            </span>
          )}
          <input
            className={`govuk-input govuk-input--width-20 ${emailErrMessage.newEmail ? 'govuk-input--error' : ''}`}
            id="newEmail"
            name="newEmail"
            type="email"
            spellCheck="false"
            aria-describedby={
              emailErrMessage.newEmail ? 'new-email-error' : undefined
            }
            autoComplete="email"
            placeholder="Enter new email address"
            value={emailDetails.newEmail}
            onChange={e =>
              setEmailDetails(prev => ({
                ...prev,
                newEmail: e.target.value,
              }))
            }
          />
        </div>
        <div
          className={`govuk-form-group ${emailErrMessage.confirmEmail ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="confirmEmail">
            Confirm new email address
          </label>
          {emailErrMessage.confirmEmail && (
            <span id="email-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>{' '}
              {emailErrMessage.confirmEmail}
            </span>
          )}
          <input
            className={`govuk-input govuk-input--width-20 ${emailErrMessage.confirmEmail ? 'govuk-input--error' : ''}`}
            id="confirmEmail"
            name="confirmEmail"
            type="email"
            spellCheck="false"
            aria-describedby={
              emailErrMessage.confirmEmail ? 'new-email-error' : undefined
            }
            autoComplete="email"
            placeholder="Enter new email address"
            value={emailDetails.confirmEmail}
            onChange={e =>
              setEmailDetails(prev => ({
                ...prev,
                confirmEmail: e.target.value,
              }))
            }
          />
        </div>
        <div className="govuk-button-group">
          <button
            type="submit"
            className="govuk-button"
            data-module="govuk-button"
            disabled={changeEmailLoading}
            onClick={onSubmit}
          >
            Save
          </button>
          <button
            type="submit"
            className="govuk-button govuk-button--secondary"
            data-module="govuk-button"
            onClick={e => onResetForm(e)}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default UpdateEmailAddress;
